import { type DashboardPeriod, DEFAULT_DASHBOARD_PERIOD } from "@repo/shared/types";
import { ChevronRight, FileSearch } from "lucide-react";
import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableCellLink,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type { DashboardTarget } from "@/lib/api/dashboard";
import { useDashboardWidget } from "@/lib/hooks/use-dashboard";
import { useT } from "@/lib/i18n/use-t";
import { useDashboardFormat } from "../use-dashboard-format";
import { T } from "./advice-to-check-widget.i18n";
import { InitialsAvatar } from "./initials-avatar";
import { PeriodSwitch } from "./period-switch";
import { WidgetCard } from "./widget-card";
import { WidgetEmpty } from "./widget-empty";

export function AdviceToCheckWidget({ target }: { target: DashboardTarget }) {
  const [period, setPeriod] = useState<DashboardPeriod>(DEFAULT_DASHBOARD_PERIOD);
  const { data, isLoading, error } = useDashboardWidget("advice_to_check", target, { period });
  const t = useT(T);
  const { shortDate } = useDashboardFormat();

  return (
    <WidgetCard
      icon={FileSearch}
      title={t.adviceToCheck}
      description={t.adviceToCheckDescription}
      actions={<PeriodSwitch value={period} onChange={setPeriod} />}
      footer={data ? { label: t.openFlowsReview, to: data.link } : undefined}
      isLoading={isLoading}
      error={error}
    >
      {data &&
        (data.items.length === 0 ? (
          <WidgetEmpty>{t.noAdvice}</WidgetEmpty>
        ) : (
          <Table variant="navigation" surface={false}>
            <TableHeader>
              <TableRow>
                <TableHead>{t.colClient}</TableHead>
                <TableHead>{t.colFlow}</TableHead>
                <TableHead>{t.colUser}</TableHead>
                <TableHead>{t.colCompleted}</TableHead>
                <TableHead>{t.colCheck}</TableHead>
                <TableHead className="w-10" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.items.map((item) => (
                <TableRow key={item.flowInstanceId}>
                  <TableCellLink to={item.link} linkClassName="flex items-center gap-3">
                    <InitialsAvatar name={item.clientName} />
                    <span className="font-medium">{item.clientName}</span>
                  </TableCellLink>
                  <TableCell>{item.flowName ?? "—"}</TableCell>
                  <TableCell className="text-muted-foreground">{item.advisorName ?? "—"}</TableCell>
                  <TableCell className="text-muted-foreground">
                    {shortDate(item.completedAt)}
                  </TableCell>
                  <TableCell>
                    <Badge variant={item.checkStatus === "flagged" ? "destructive" : "secondary"}>
                      {item.checkStatus === "flagged" ? t.flagged : t.notReviewed}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ))}
    </WidgetCard>
  );
}
